import type { PortalNotification } from '../../core/models/portal-notification';

/**
 * Where a notification leads, or `null` when it leads nowhere.
 *
 * The row carries the record it points at — `entityType` and `entityId` have been on the projection
 * since the table was written — and until now the panel rendered a sentence about a case with no
 * way to reach it.
 *
 * **The type decides before the entity does, for exactly one type.** A staff reply is stored
 * against the case it was asked on, because that is what it is about; but the questions channel is
 * one thread per customer and lives on its own tab, so `MessageReceived` goes there rather than to
 * the case page, which does not show the thread at all. Everything else is the entity it names.
 *
 * A consultation reaches the appointments tab rather than a page of its own, because the portal has
 * no per-appointment route. Landing on the list is honest; inventing a URL that renders nothing is
 * not.
 */
export function notificationLink(notification: PortalNotification, language: string): string | null {
    const portal = `/${language}/portal`;

    if (notification.type === 'MessageReceived') {
        return `${portal}/messages`;
    }

    switch (notification.entityType) {
        case 'ClientCase':
            return notification.entityId === null ? null : `${portal}/cases/${notification.entityId}`;

        case 'Appointment':
            return `${portal}/appointments`;

        default:
            return null;
    }
}
